"use client";

import React, { createContext, useContext } from "react";
import {
  ProfileContextType,
  useProfileContext,
} from "@/providers/profile-provider/profile-provider";
import { trialConvertsCount } from "@/services/license/license-limiters/service-using-count-limiters/convert-using-count-limiter";
import { useQuery, useQueryClient } from "react-query";
import { SubscriptionLimitationsData } from "@/app/api/subscription-limitations/route";

interface SubscriptionLimitationsContextType extends ProfileContextType {
  convertsLeft: number | null;
  convertsCount: number;
  setConvertsLeft: (value: number | null) => void;
  convertsMax: number | null;
}

type LimitationsContext = {
  convertsCount: number;
  convertsLeft: number | null;
  convertsMax: number | null;
};

const SubscriptionLimitationsContext =
  createContext<SubscriptionLimitationsContextType | null>(null);

export const useSubscriptionLimitationsContext = () => {
  const context = useContext(SubscriptionLimitationsContext);
  if (!context) {
    throw new Error(
      "useSubscriptionLimitationsContext must be used within a SubscriptionLimitationsProvider",
    );
  }
  return context;
};

const SubscriptionLimitationsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const {
    user,
    license,
    isLoading: isProfileLoading,
    isLicenseFetched,
    ...profileProps
  } = useProfileContext();

  const queryClient = useQueryClient();
  const queryKey = ["subscription-limitations", user?.id, license];

  const fetchUserConverts = async (userId?: string) => {
    let convertsCount = 0;
    let convertsLeft: number | null = trialConvertsCount;
    let convertsMax: number | null = trialConvertsCount;

    if (userId) {
      const res = await fetch("/api/subscription-limitations");

      if (res.ok) {
        const subscriptionLimitationsData =
          (await res.json()) as SubscriptionLimitationsData;

        convertsCount = subscriptionLimitationsData.convertsCount;
        convertsLeft = subscriptionLimitationsData.convertsLeft;
        convertsMax = subscriptionLimitationsData.convertsMax;
      }
    }

    return { convertsCount, convertsLeft, convertsMax };
  };

  const { data, isLoading: isLimitationLoading } = useQuery<LimitationsContext>(
    queryKey,
    () => fetchUserConverts(user?.id),
    {
      enabled: isLicenseFetched,
      onError: (err) =>
        console.error("Failed to fetch license limitation:", err),
    },
  );

  const setConvertsLeft = (value: number | null) => {
    if (data) {
      queryClient.setQueryData<LimitationsContext>(queryKey, {
        ...data,
        convertsLeft: value,
      });
    }
  };

  const isLoading = isProfileLoading || isLimitationLoading;

  return (
    <SubscriptionLimitationsContext.Provider
      value={{
        convertsLeft: data?.convertsLeft ?? null,
        setConvertsLeft,
        convertsMax: data?.convertsMax ?? null,
        user,
        license,
        convertsCount: data?.convertsCount ?? 0,
        isLicenseFetched,
        ...profileProps,
        isLoading,
      }}
    >
      {children}
    </SubscriptionLimitationsContext.Provider>
  );
};

export default SubscriptionLimitationsProvider;
